/**
 * Parsea un string separado por delimiters a un array
 * @param {string} str - String a parsear
 * @param {string[]} delimiters - Array de delimitadores a probar (por defecto [';', ','])
 * @returns {string[]} Array de strings parseados
 */
export function parseStringToArray(str, delimiters = [';', ',']) {
  if (!str || typeof str !== 'string') {
    return [];
  }
  
  // Probar cada delimitador
  for (const delimiter of delimiters) {
    if (str.includes(delimiter)) {
      return str
        .split(delimiter)
        .map(item => item.trim())
        .filter(item => item.length > 0);
    }
  }
  
  // Si no hay delimitador, retornar el string como array de un elemento si no está vacío
  const trimmed = str.trim();
  return trimmed.length > 0 ? [trimmed] : [];
}

/**
 * Parsea y normaliza la estructura de CustomerExperience
 * @param {object} customerExperience - Objeto CustomerExperience del backend
 * @returns {object} Objeto normalizado con feedbackCX, fortalezas, oportunidades, conclusion
 */
export function parseCustomerExperience(customerExperience) {
  if (!customerExperience || typeof customerExperience !== 'object') {
    return {
      feedbackCX: { satisfaccion: null, emociones: [], frasesClave: [] },
      fortalezas: { habilidades: [], buenasPracticas: [] },
      oportunidades: { aspectosAMejorar: [], recomendaciones: [] },
      conclusion: { texto: null }
    };
  }

  const feedbackCX = customerExperience.FeedbackCX || {};
  const fortalezaAgente = customerExperience.FortalezaAgente || {};
  const oportunidadesAgente = customerExperience.OportunidadesAgente || {};

  return {
    feedbackCX: {
      satisfaccion: feedbackCX.Satisfaccion || null,
      emociones: Array.isArray(feedbackCX.Emociones) ? feedbackCX.Emociones : parseStringToArray(feedbackCX.Emociones),
      frasesClave: Array.isArray(feedbackCX.FrasesClave) ? feedbackCX.FrasesClave : parseStringToArray(feedbackCX.FrasesClave),
    },
    fortalezas: {
      habilidades: Array.isArray(fortalezaAgente.Habilidades) ? fortalezaAgente.Habilidades : parseStringToArray(fortalezaAgente.Habilidades),
      buenasPracticas: Array.isArray(fortalezaAgente.BuenasPracticas) ? fortalezaAgente.BuenasPracticas : parseStringToArray(fortalezaAgente.BuenasPracticas),
    },
    oportunidades: {
      aspectosAMejorar: Array.isArray(oportunidadesAgente.AspectosAMejorar) ? oportunidadesAgente.AspectosAMejorar : parseStringToArray(oportunidadesAgente.AspectosAMejorar),
      recomendaciones: Array.isArray(oportunidadesAgente.Recomendaciones) ? oportunidadesAgente.Recomendaciones : parseStringToArray(oportunidadesAgente.Recomendaciones),
    },
    conclusion: {
      texto: typeof customerExperience.Conclusion === 'string' ? customerExperience.Conclusion : null,
    }
  };
}

/**
 * Parsea y normaliza la estructura anidada de ContactSummary
 * @param {object} contactSummary - Objeto ContactSummary con estructura anidada
 * @returns {object} Objeto normalizado con feedbackCX, fortalezas, oportunidades, conclusion
 */
export function parseContactSummary(contactSummary) {
  if (!contactSummary || typeof contactSummary !== 'object') {
    return {
      feedbackCX: {
        satisfaccion: null,
        emociones: [],
        frasesClave: []
      },
      fortalezas: {
        habilidades: [],
        buenasPracticas: []
      },
      oportunidades: {
        aspectosAMejorar: [],
        recomendaciones: []
      },
      conclusion: {
        texto: null
      }
    };
  }

  // Navegar la estructura anidada: insight.ContactSummary.insight
  let insight = contactSummary;
  
  // Primer nivel: insight
  if (insight.insight && typeof insight.insight === 'object') {
    insight = insight.insight;
  }
  
  // Segundo nivel: ContactSummary
  if (insight.ContactSummary && typeof insight.ContactSummary === 'object') {
    insight = insight.ContactSummary;
    
    // Tercer nivel: insight dentro de ContactSummary
    if (insight.insight && typeof insight.insight === 'object') {
      insight = insight.insight;
    }
  }

  // Extraer Feedback_CX
  const feedbackCX = insight.Feedback_CX || {};
  const satisfaccion = feedbackCX.Satisfaccion || null;
  const emocionesStr = feedbackCX.Emociones || '';
  const frasesClaveStr = feedbackCX.Frases_clave || '';

  // Extraer Fortaleza_Agente
  const fortalezaAgente = insight.Fortaleza_Agente || {};
  const habilidadesStr = fortalezaAgente.Habilidades || '';
  const buenasPracticasStr = fortalezaAgente.Buenas_practicas || '';

  // Extraer Oportunidades_Agente
  const oportunidadesAgente = insight.Oportunidades_Agente || {};
  const aspectosAMejorarStr = oportunidadesAgente.Aspectos_a_mejorar || '';
  const recomendacionesStr = oportunidadesAgente.Recomendaciones || '';

  // Extraer Conclusion
  const conclusion = insight.Conclusion || {};
  const conclusionTexto = conclusion.Texto || null;

  return {
    feedbackCX: {
      satisfaccion: satisfaccion,
      emociones: parseStringToArray(emocionesStr),
      frasesClave: parseStringToArray(frasesClaveStr)
    },
    fortalezas: {
      habilidades: parseStringToArray(habilidadesStr),
      buenasPracticas: parseStringToArray(buenasPracticasStr)
    },
    oportunidades: {
      aspectosAMejorar: parseStringToArray(aspectosAMejorarStr),
      recomendaciones: parseStringToArray(recomendacionesStr)
    },
    conclusion: {
      texto: conclusionTexto
    }
  };
}
